import { motion, useReducedMotion } from "framer-motion";
import { Zap, CheckCircle2 } from "lucide-react";
import { CountUp } from "@/features/dashboard/components/CountUp";

interface ResponseSpeedCardProps {
  firstResponseSeconds: number;
  firstResponseTrendPct: number;
  resolutionMinutes: number;
  resolutionTrendPct: number;
}

/**
 * Companion card to the Response Time trend chart: first-response time
 * (time to first reply, almost always the AI) vs. full resolution time
 * (time until the conversation is fully closed out, AI or human).
 */
export function ResponseSpeedCard({
  firstResponseSeconds,
  firstResponseTrendPct,
  resolutionMinutes,
  resolutionTrendPct,
}: ResponseSpeedCardProps) {
  const prefersReducedMotion = useReducedMotion();
  // Both metrics are "lower is better" — resolution takes ~10x longer than
  // first response, so bar widths are scaled against a shared ceiling rather
  // than against each other directly.
  const ceilingSeconds = Math.max(resolutionMinutes * 60, firstResponseSeconds * 20);
  const firstResponsePct = Math.min(100, (firstResponseSeconds / ceilingSeconds) * 100);
  const resolutionPct = Math.min(100, ((resolutionMinutes * 60) / ceilingSeconds) * 100);

  const rows = [
    {
      key: "first",
      label: "First Response",
      icon: Zap,
      value: firstResponseSeconds,
      suffix: "s",
      decimals: 1,
      trendPct: firstResponseTrendPct,
      widthPct: firstResponsePct,
      color: "bg-primary",
    },
    {
      key: "resolution",
      label: "Full Resolution",
      icon: CheckCircle2,
      value: resolutionMinutes,
      suffix: "m",
      decimals: 1,
      trendPct: resolutionTrendPct,
      widthPct: resolutionPct,
      color: "bg-info",
    },
  ];

  return (
    <div className="flex h-64 flex-col justify-center gap-6 md:h-72">
      {rows.map((row) => {
        const Icon = row.icon;
        const isGood = row.trendPct <= 0;
        return (
          <div key={row.key}>
            <div className="mb-1.5 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                {row.label}
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
                  <CountUp value={row.value} suffix={row.suffix} decimals={row.decimals} />
                </span>
                <span className={isGood ? "text-xs font-medium text-success" : "text-xs font-medium text-error"}>
                  {row.trendPct <= 0 ? "" : "+"}
                  {row.trendPct}%
                </span>
              </div>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <motion.div
                className={`h-full rounded-full ${row.color}`}
                initial={{ width: prefersReducedMotion ? `${row.widthPct}%` : 0 }}
                animate={{ width: `${row.widthPct}%` }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        );
      })}
      <p className="text-[11px] leading-snug text-muted-foreground">
        First response is almost always the AI agent; full resolution includes any
        follow-up or human handoff before the conversation is closed.
      </p>
    </div>
  );
}
